<i18n>
{
	"en": {
		"title": "Deviations",
		"mustSaveOrder": "The order must be saved before a deviation can be connected to it.",
		"noDeviations": "There are no registered deviations.",
		"addDeviation": "Add new deviation"
	},

	"no": {
		"title": "Avvik",
		"mustSaveOrder": "Oppdraget må lagres før avvik kan tilknyttes.",
		"noDeviations": "Det er ingen registrerte avvik.",
		"addDeviation": "Legg til nytt avvik"
	}
}
</i18n>

<template>
	<div class="deviations">
		<div class="deviations-header">
			<t-panel-header
				:text="$t('title')"
				icon="warning-sign"
				type="bp"
				size="md"
			/>
			<t-icon-button
				icon="plus"
				type="bp"
				appearance="minimal"
				tabindex="-1"
				:disabled="!orderIsSaved"
				@click="addDeviation"
			/>
		</div>

		<DeviationGrid
			v-if="deviations && deviations.length > 0"
			:deviations="deviations"
			:style="`height: ${gridHeight}px`"
			@rowDoubleClick="editDeviation"
		/>

		<t-no-content-panel
			v-else
			:text="$t('noDeviations')"
			icon="warning-sign"
			iconType="bp"
			class="no-content"
			noBetweenGap
		/>

		<ImageViewer :isShown="showImageViewer" :images="selectedDeviationPictures"  @close="closeImageViewer"/>
	</div>
</template>

<script>
import { newUUID } from '@/core';
import ImageViewer from '@/modules/imageViewer/ImageViewer';
import DeviationGrid from '@/modules/deviations/DeviationGrid';
import { mapActions as mapOrderActions } from '../store/newOrder.store.js';
import { mapActions as mapDeviationActions } from '@/modules/newDeviation/store/newDeviation.store.js';

export default {
	name: 'Deviations',
	components: {
		ImageViewer,
		DeviationGrid,
	},
	props: { order: Object, events: Object },

	data() {
		return {
			defaultColumnDefinition: { resizable: false, },
			selectedDeviation: undefined,
			showImageViewer: false
		};
	},

	computed: {
		orderIsSaved() {
			return Boolean(this.order && this.order.id);
		},
		deviations() {
			return this.order.deviationBindings &&
				this.order.deviationBindings.map(binding => binding.deviation);
		},
		gridHeight() {
			return (38 + this.order.deviationBindings.length * 40);
		},
		addButtonStyle() {
			return this.deviations && this.deviations.length ? '' : 'margin: auto';
		},
		selectedDeviationPictures() {
			return this.selectedDeviation ? this.selectedDeviation.pictures : [];
		}
	},

	methods: {
		...mapOrderActions(['setDeviationBindings']),
		...mapDeviationActions([
			'returnDeviation',
			'newDeviation',
			'loadDeviation',
		]),
		async editDeviation(deviationId) {
			let updatedDeviation = await (await this.returnDeviation(this.loadDeviation))(deviationId);
			if (!updatedDeviation) return;
			let newDeviationBindings = this.order.deviationBindings.map(binding =>
				binding.deviationId === updatedDeviation.id ? this.createDeviationBinding(updatedDeviation) : binding);
			this.setDeviationBindings(newDeviationBindings);
		},
		async addDeviation() {
			let order = { ...this.order };
			delete order.deviationBindings; // Avoid any circular referencing

			let deviationPreset = {
				orderBindings: [this.createOrderBinding(order)],
				customer: order.customer || {},
				goodsType: order.goodsType || {},
			};

			let newDeviation = await (await this.returnDeviation(this.newDeviation))(deviationPreset);
			if (newDeviation && newDeviation.id) {
				this.setDeviationBindings([...this.order.deviationBindings, this.createDeviationBinding(newDeviation)]);
			}
		},

		createOrderBinding(order) {
			return { order, orderId: order.id, id: newUUID() };
		},
		createDeviationBinding(deviation) {
			return { ...deviation.orderBindings.find(ob => ob.orderId === this.order.id), order: undefined, deviation };
		},

		openImageViewer(item) {
			this.selectedDeviation = item;
			this.showImageViewer = true;
		},
		closeImageViewer() {
			this.showImageViewer = false;
			this.selectedDeviation = undefined;
		},
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.deviations {
	height: 100%;
	min-height: inherit;
}

.deviations-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 15px;
	background: white;
}

.no-content {
	width: 100%;
	height: 144px;
}
</style>
